import { useQuery } from "react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useModalContext } from "components/modal";
import { openLinkItemModal } from "../../utils/openLinkItemModal";

/** A downloaded catalog row this vendor has never carried, as the API returns it. */
interface UnlinkedHistoryRow {
  heritage_part_number: string;
  mfg_part_number: string | null;
  description: string | null;
  supplier_name: string | null;
  heritage_image_url: string | null;
  /** Set when the part matches existing inventory (#21 tiers) despite never being ordered. */
  matched_item_id?: number | null;
}

/**
 * F1 (ESERV2-1443) — the "never ordered" half of Sanjay's 08-19 ask: the vendor
 * page shows ALL downloaded Heritage history, and a row that is not among the
 * vendor's items says so instead of being invisible. The linked half carries a
 * star on its rows (VendorItemDetail); these rows carry the "Never ordered"
 * tag and a Link action into the existing link screen.
 */
const UnlinkedHeritageHistory = ({ vendorId }: { vendorId: number | null | undefined }) => {
  const { t } = useTranslation();
  const tBase = "views.vendors.VendorBody.VendorItems.UnlinkedHeritageHistory";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { setModal } = useModalContext();

  const historyQuery = useQuery<UnlinkedHistoryRow[]>(
    ["heritage-unlinked-history", vendorId],
    () =>
      axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/vendors/${vendorId}/unlinked-history`
        )
        .then((res) => res.data.items || []),
    { enabled: vendorId != null }
  );

  const rows = historyQuery.data ?? [];
  if (historyQuery.isLoading || rows.length === 0) {
    // Nothing downloaded beyond what is linked — the section earns its space
    // only when there is something to say.
    return null;
  }

  return (
    <div className="mt-4 border-t pt-3">
      <div className="text-sm font-semibold text-gray-700 mb-2">
        {tr("Downloaded from Heritage — not in your items")} ({rows.length})
      </div>
      {rows.map((row) => (
        <div
          key={row.heritage_part_number}
          className="flex items-center gap-3 border rounded p-2 mb-2 bg-gray-50"
        >
          {/* Heritage's own product image — licensed for Heritage screens only. */}
          {row.heritage_image_url && (
            <img
              src={row.heritage_image_url}
              alt=""
              loading="lazy"
              className="h-10 w-10 flex-shrink-0 rounded border border-gray-200 object-contain bg-white"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-800">
              {row.heritage_part_number}
              <span className="ml-2 inline-block text-[10px] uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 align-middle">
                {tr("Never ordered")}
              </span>
            </div>
            <div className="text-xs text-gray-600 truncate">{row.description || "—"}</div>
            {row.matched_item_id != null && (
              <div className="text-[11px] text-gray-500">
                {tr("Matches an item already in your inventory")}
              </div>
            )}
          </div>
          <button
            type="button"
            className="text-xs font-medium text-blue-600 hover:underline min-h-[44px] px-3 touch-manipulation shrink-0"
            onClick={() =>
              openLinkItemModal(setModal, {
                vendorId,
                isSiteoneVendor: false,
                isHeritageVendor: true,
                label: tr("Link Item"),
              })
            }
          >
            {tr("Link")}
          </button>
        </div>
      ))}
    </div>
  );
};

export default UnlinkedHeritageHistory;
